import { useState } from 'react';
import { UM } from './tokens';
import type { SidebarMenu } from './types';

// ─── Menu items ──────────────────────────────────────────────────────────────

const MENU_ITEMS: { id: SidebarMenu; icon: string; label: string }[] = [
  { id: 'layers', icon: '◇', label: 'Couches' },
  { id: 'geometries', icon: '⚙', label: 'Géométries' },
  { id: 'measures', icon: '📐', label: 'Mesures' },
  { id: 'filters', icon: '≡', label: 'Filtres' },
  { id: 'selection', icon: '📋', label: 'Sélection' },
  { id: 'export', icon: '↓', label: 'Export' },
  { id: 'import', icon: '↑', label: 'Import' },
];

// ─── Component ───────────────────────────────────────────────────────────────

interface UMSidebarProps {
  activeMenu: SidebarMenu | null;
  onToggleMenu: (menu: SidebarMenu) => void;
}

export function UMSidebar({ activeMenu, onToggleMenu }: UMSidebarProps) {
  return (
    <nav
      style={{
        width: 64,
        minWidth: 64,
        background: UM.bg,
        borderRight: `1px solid ${UM.border}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        paddingTop: 8,
        gap: 2,
        zIndex: 20,
      }}
    >
      {/* Logo */}
      <div
        style={{
          width: 40,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 700,
          fontSize: 12,
          color: UM.accent,
          marginBottom: 8,
        }}
      >
        UM
      </div>

      {MENU_ITEMS.map((item) => (
        <button
          key={item.id}
          onClick={() => onToggleMenu(item.id)}
          title={item.label}
          style={{
            width: 56,
            height: 56,
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            fontSize: 10,
            color: activeMenu === item.id ? UM.accent : UM.textMuted,
            background: activeMenu === item.id ? UM.accentLight : 'transparent',
            fontWeight: activeMenu === item.id ? 600 : 400,
            borderLeft: activeMenu === item.id ? `3px solid ${UM.accent}` : '3px solid transparent',
          }}
        >
          <span style={{ fontSize: 18 }}>{item.icon}</span>
          {item.label}
        </button>
      ))}

      <div style={{ flex: 1 }} />
      <button
        style={{
          width: 56,
          height: 56,
          border: 'none',
          borderRadius: 8,
          cursor: 'pointer',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          fontSize: 10,
          color: UM.textMuted,
          background: 'transparent',
          marginBottom: 8,
        }}
      >
        <span style={{ fontSize: 18 }}>⚙</span>
        Admin
      </button>
    </nav>
  );
}

// ─── Expandable sidebar panel ─────────────────────────────────────────────────

interface UMSidebarPanelProps {
  activeMenu: SidebarMenu;
  onClose: () => void;
  children?: React.ReactNode;
}

export function UMSidebarPanel({ activeMenu, onClose, children }: UMSidebarPanelProps) {
  const menuLabel = MENU_ITEMS.find((m) => m.id === activeMenu)?.label ?? '';

  return (
    <aside
      style={{
        width: 340,
        minWidth: 340,
        background: UM.bg,
        borderRight: `1px solid ${UM.border}`,
        overflowY: 'auto',
        padding: 16,
        zIndex: 15,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: UM.text }}>
          {menuLabel}
        </h2>
        <button
          onClick={onClose}
          style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 18, color: UM.textMuted }}
        >
          ✕
        </button>
      </div>
      {children ?? (
        <p style={{ color: UM.textMuted, fontSize: 13 }}>
          Contenu du panneau « {menuLabel} »
        </p>
      )}
    </aside>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useUMSidebar() {
  const [activeMenu, setActiveMenu] = useState<SidebarMenu | null>(null);
  const toggleMenu = (menu: SidebarMenu) => setActiveMenu((prev) => (prev === menu ? null : menu));
  const closeMenu = () => setActiveMenu(null);
  return { activeMenu, toggleMenu, closeMenu };
}
